'use client'

import type { CSSProperties, ReactNode } from 'react'
import type { BirdieCardView } from 'src/components/BirdieRoundCard/birdieCardModel'
import type { WinTier } from 'src/components/ChipPlacementPanel/WinPill'
import './birdie-board.scss'
import type { AvatarRingView } from './avatarRing'
import { BirdieCard } from './BirdieCard'
import { BirdieTile } from './BirdieTile'
import type { BirdieChips } from './birdieChips'
import type { BoardColumn } from './boardGeometry'
import {
  TILE_ENTER_STEP_MS,
  TILE_WAVE_STEP_MS,
  cascadeDelaysMs,
  centreOf,
  type BoardPoint
} from './boardMotion'
import { BoardHeadIcon } from './BoardHeadIcon'
import type { CapsuleStateView } from './capsuleState'
import { BirdieBallDefs } from './GolfBall'
import type { ChipStyle } from './chipPalette'
import type { BirdieRingsView } from './livePutts'
import { IDLE_LIVE_STATUS, tileLife, type LiveStatus } from './liveStatus'
import type { BirdieTileView } from './tileViewModel'

type Props = Readonly<{
  /**
   * Optional (B11 item 8 / review 17): a round that carries no odds has no card
   * view, and the mobile board used to render NOTHING at all — no tiles, no
   * plaque and, crucially, no notice, so a phone player saw an empty column
   * with no explanation while desktop showed "Waiting for this round's odds".
   * The strip is what is optional; the board is not.
   */
  card?: BirdieCardView
  /** Which shape the capsule takes, and what may animate (B16). */
  capsule: CapsuleStateView
  /** The avatar countdown — the phone gets the same clock as the desktop board. */
  ring: AvatarRingView
  tiles: readonly BirdieTileView[]
  placement: BirdieChips
  /** The two column headings, already localised — the desktop board's, verbatim. */
  headings: Readonly<Record<BoardColumn, string>>
  /** The ladder colours for a placed total — see `chipStyleForAmount`. */
  chipStyleForAmount: (amount: number) => ChipStyle
  tilesLocked: boolean
  /** B34 live ticket status, from `liveStatus(tiles, phase.livePutts)`. */
  liveStatus?: LiveStatus
  /** B40: the desktop board's own cascade key — see `ArcOverlayProps`. */
  enterKey?: string
  /** B40: how many putts are on screen, 0-3 — `phase.livePutts.length`. */
  puttsSeen?: number
  /**
   * B40: the revealed round type. The phone draws no weather layer (see the
   * comment where the callout is rendered) but it does light the golfer's bezel
   * with it — that is a state, and a 44-px strip can carry a state.
   */
  roundType?: 'frost' | 'fire'
  /** B40: the golfer's thought, when he is having one — see `BirdieCard`. */
  thought?: { text: string; moment: string }
  /** B32: the tiles an armed auto-bet zone passes over — the desktop board's set, verbatim. */
  zoneMissIds?: ReadonlySet<string>
  /** B34: the putt callout pill, positioned by the stack's own rule. */
  callout?: ReactNode
  /**
   * The past-rounds rail — the flag button and the round discs — which on a
   * phone rides the capsule's row instead of a band of its own above the stream
   * (owner 2026-09-17, for the vertical space). The page passes the SAME
   * `<ShowRaceResults>` every game mounts; only this layout re-homes it, so the
   * desktop board and the other twelve games are untouched. StackBoard renders
   * on the phone alone, which is what makes that gate free.
   */
  railSlot?: ReactNode
  receivingOptionId: string | null
  /** Same two-source ring row the desktop card draws (live putts, then the
   *  settled pattern) — mobile shows the same three slots, not a growing strip. */
  rings?: BirdieRingsView
  winnerIds: ReadonlySet<string>
  betByOptionId: ReadonlyMap<string, { won: boolean; lost: boolean; payout: number; tier: WinTier }>
  currencySymbol: string
  notice: { message: string; tone: 'warning' | 'info' | 'error' } | null
  chipLabel: (amount: number) => string
  /**
   * B38: the options whose stake is a free bet, and the localised word its
   * chip carries. Both arrive already decided — the claim map is the host's
   * and `t` lives there, exactly as with the column headings.
   */
  freeBetIds?: ReadonlySet<string>
  freeBetLabel?: string
  stakeDescription?: (input: {
    amount: number
    won: boolean
    lost: boolean
    free?: boolean
  }) => string
  onPlace: (optionId: string, target: DOMRect) => void
  onRemove: (optionId: string) => void
  /** B28: passed straight to every tile — see `BirdieTileProps`. */
  onLockedPress?: (optionId: string) => void
}>

/** Stagger between the winning tiles' pills, ms — the same cascade as the arc. */
const WIN_STAGGER_MS = 80

/**
 * How many tiles each block reads across. Structure, not size: the two markets
 * have different shapes (a COUNT bar is balls-left / price-right, an EXACT tile
 * is a centred stack), so the grid track count stays with the component while
 * every height lives in the stylesheet.
 */
const COUNT_COLUMNS = 2
const EXACT_COLUMNS = 3
/**
 * With the round-type pair present the any-order block reads three across: its four options fill the
 * first two columns and frost / fire take the third, one per row (owner 2026-09-16, "to the right of
 * the any order"). The desktop board puts them in row 2 of the same column instead — the phone has
 * width to spare where the desktop has height to spare.
 */
const COUNT_COLUMNS_WITH_ROUND = 3

/**
 * The phone reads the any-order block up from ONE (owner 2026-09-17): one in,
 * two in, all three, and none-in last. The board's own order is the marble
 * map's — three down to none — which on a phone put the rarest option in the
 * corner a thumb reaches first. Desktop keeps the map's order; this is the
 * stack's own reading and nothing else consumes it.
 */
const PHONE_COUNT_ORDER: readonly string[] = ['IN1', 'IN2', 'IN3', 'IN0']

const byPhoneCountOrder = (a: BirdieTileView, b: BirdieTileView): number => {
  const ai = PHONE_COUNT_ORDER.indexOf(a.key)
  const bi = PHONE_COUNT_ORDER.indexOf(b.key)
  // A key the order does not name keeps its place behind the ones it does.
  if (ai === -1 || bi === -1) return ai === bi ? 0 : ai === -1 ? 1 : -1
  return ai - bi
}

/**
 * Mobile: the golfer capsule at 44 px, COUNT as a 2-column block and EXACT as a
 * 3-column block, below the stream. Same capsule, same tiles, same glyphs, same
 * reading rules as desktop — only the placement changes (spec, Mobile; owner:
 * "web first, keep mobile simple as a start").
 *
 * B30: CSS sizes the stack. Every height — the capsule strip, the two tile
 * families and their floors on a small phone — is a custom property on
 * `.birdie-stack` in `birdie-board.scss`, so this component measures nothing,
 * takes no viewport, and carries no inline height. The stream yields to whatever
 * the board turns out to be: the shared `useMobileStreamHeight` observes
 * `.marbles-race__bottom` (the panel this board renders into) and writes
 * `--bottom-panel-height`, which is what `.dolby-feed`'s height subtracts.
 *
 * `.birdie-tile` is `position: absolute` for the arc; the stack neutralises that
 * with a wrapper-scoped rule (`.birdie-stack .birdie-tile`, birdie-board.scss),
 * NOT with a per-tile prop — `BirdieTile` is B3's shipped contract and takes no
 * className. The tile is otherwise rendered exactly as the arc renders it,
 * including B28's refusal path: a locked tile is no longer natively disabled,
 * so it takes the tap and answers it. What refuses the chip is the TILE's own
 * `if (locked)` — `birdieChipReducer` guards the price, the balance and the
 * per-option cap and knows nothing about the phase, so the handler is the
 * authority on a locked round and this is the sentence to read before deleting
 * that branch.
 */
export function StackBoard(props: Props) {
  const live = props.liveStatus ?? IDLE_LIVE_STATUS
  const count = props.tiles.filter((tile) => tile.kind === 'count').sort(byPhoneCountOrder)
  const exact = props.tiles.filter((tile) => tile.kind === 'exact')
  const round = props.tiles.filter((tile) => tile.kind === 'round')
  // Row-major fill, so slotting one round-type tile after every pair of any-order tiles puts the
  // pair in the third column rather than trailing the block.
  const countRow: BirdieTileView[] = []
  if (round.length === 0) {
    countRow.push(...count)
  } else {
    const rows = Math.max(Math.ceil(count.length / COUNT_COLUMNS), round.length)
    for (let r = 0; r < rows; r++) {
      countRow.push(...count.slice(r * COUNT_COLUMNS, r * COUNT_COLUMNS + COUNT_COLUMNS))
      const extra = round[r]
      if (extra) countRow.push(extra)
    }
  }

  const card = props.card

  /**
   * B40 — the phone's own grid positions, for the same two beats the desktop
   * board ranks on real boxes.
   *
   * It ranks on `{ column, row }` rather than on pixels because the stack is
   * sized entirely by CSS (B30) and this component measures nothing — but it is
   * still a 2-D rank, so the cascade opens out of the middle of the BOARD the way
   * the desktop one does, rather than running along the options array. The two
   * blocks are one sequence: the any-order rows first, then the exact rows under
   * them, which is the order they are drawn in.
   */
  const countColumns = round.length > 0 ? COUNT_COLUMNS_WITH_ROUND : COUNT_COLUMNS
  const countRows = Math.ceil(countRow.length / countColumns)
  const points: BoardPoint[] = []
  for (let index = 0; index < countRow.length; index++) {
    points.push({ x: index % countColumns, y: Math.floor(index / countColumns) })
  }
  for (let index = 0; index < exact.length; index++) {
    points.push({ x: index % EXACT_COLUMNS, y: countRows + Math.floor(index / EXACT_COLUMNS) })
  }
  const middle = centreOf(points)
  const enterDelays = cascadeDelaysMs(points, middle, TILE_ENTER_STEP_MS)
  let waveOrigin = middle
  const ordered: BirdieTileView[] = countRow.concat(exact)
  for (let index = 0; index < ordered.length; index++) {
    const tile = ordered[index]
    const point = points[index]
    if (tile === undefined || point === undefined) continue
    if (props.winnerIds.has(tile.optionId)) {
      waveOrigin = point
      break
    }
  }
  const waveDelays = cascadeDelaysMs(points, waveOrigin, TILE_WAVE_STEP_MS)

  const renderTile = (tile: BirdieTileView, index: number) => {
    const chip = props.placement[tile.optionId]
    const bet = props.betByOptionId.get(tile.optionId)
    return (
      <BirdieTile
        beat={props.puttsSeen ?? 0}
        chipLabel={props.chipLabel}
        {...(chip ? { chipStyle: props.chipStyleForAmount(chip.amount) } : {})}
        currencySymbol={props.currencySymbol}
        enterDelayMs={enterDelays[index] ?? 0}
        enterKey={props.enterKey}
        freeBet={props.freeBetIds?.has(tile.optionId) === true}
        freeBetLabel={props.freeBetLabel}
        key={tile.optionId}
        locked={props.tilesLocked}
        life={tileLife(live, tile.optionId)}
        hot={live.hotIds.has(tile.optionId)}
        myBetLost={bet?.lost === true}
        myBetWon={bet?.won === true}
        nearMiss={live.nearMissIds.has(tile.optionId)}
        onLockedPress={props.onLockedPress}
        onPlace={props.onPlace}
        onRemove={props.onRemove}
        placedAmount={chip?.amount}
        placedCount={chip?.count ?? 0}
        receiving={props.receivingOptionId === tile.optionId}
        shineIndex={index}
        stakeDescription={props.stakeDescription}
        view={tile}
        waveDelayMs={waveDelays[index] ?? 0}
        winDelayMs={index * WIN_STAGGER_MS}
        winPayout={bet?.payout ?? 0}
        winTier={bet?.tier ?? 'win'}
        winner={props.winnerIds.has(tile.optionId)}
        zoneMiss={props.zoneMissIds?.has(tile.optionId) === true}
      />
    )
  }

  /**
   * The same two labels the desktop board carries, over the same two blocks —
   * the phone reads left-to-right instead of left-column / right-column, so
   * both headings hang off the START of their block and both icons lead.
   * `.birdie-board__head` is the desktop board's class: the stack neutralises
   * its absolute positioning in a wrapper-scoped rule, exactly as it already
   * does for `.birdie-tile`.
   */
  const head = (column: BoardColumn) => (
    <div className={`birdie-board__head birdie-board__head--${column}`}>
      <BoardHeadIcon column={column} />
      <span className="birdie-board__head-text">{props.headings[column]}</span>
    </div>
  )

  /**
   * Header and grid are ONE flex item, not two. The stack's 6-px gap falls
   * between the card and each BLOCK — the header's own 2 px is inside the block
   * — and an unwrapped header would put a third and fourth 6-px gap in the
   * column, which on a 360x640 phone is the difference between a board that
   * fits above the stream and a page that scrolls.
   */
  const block = (column: BoardColumn, grid: ReactNode) => (
    <div className={`birdie-stack__block birdie-stack__block--${column}`}>
      {head(column)}
      {grid}
    </div>
  )

  /**
   * No tile block at all — an empty `tiles` (no odds, or none of them bettable),
   * which also means no capsule, since both come from the same `roundOdds`.
   * The stylesheet keeps the notice IN FLOW in that state: out of flow it had
   * nothing to sit over, so the pill hung out of a 16-px stack and over
   * whatever came next — and it is the one state whose whole content IS the
   * notice (B11 item 8). Nothing can be tapped here, so nothing re-measures the
   * panel, which is what the out-of-flow rule exists for.
   */
  const noTiles = countRow.length === 0 && exact.length === 0

  return (
    <div className={noTiles ? 'birdie-stack birdie-stack--no-tiles' : 'birdie-stack'}>
      {/*
        B40 item 8 is DESKTOP-ONLY, and the reason is paint order rather than
        budget. The weather is an absolutely positioned layer at z-index 0, which
        sits above every NON-positioned in-flow sibling — and on the phone the
        capsule is `position: static` (it has to be, to neutralise the desktop
        card's `absolute`), as are the two block wrappers. Lifting the board's
        content over the layer means either re-anchoring the nine absolutely
        positioned parts of the capsule to a new containing block, or making the
        stack a stacking context inside a panel it currently shares with the chip
        strip. Neither is a change this effect is worth: the phone keeps the
        round-type TILES, which are where the fact actually lives.
      */}
      {props.callout ?? null}
      {/* The gradients the stack's balls are painted with — idempotent, see GolfBall.tsx. */}
      <BirdieBallDefs />

      <div className="birdie-stack__top">
        {props.railSlot ?? null}
        {card ? (
          // B12: mobile uses the SAME capsule as the desktop overlay, at the 44 px
          // height the stylesheet gives it — one component, one set of states, one
          // score row, so the phone can never drift from the web.
          //
          // B16b gave the capsule two BOXES and two shapes, and the phone takes
          // neither: it keeps ONE 44-px strip in every state, in the flow, with
          // no centre-top move — there is no frame to be centred on. The desktop
          // betting capsule is 201 reference px tall, and a strip that changed
          // height every time a round opened would shove the stream up and down
          // twice a minute — a layout shift the polish bar forbids outright, and
          // one the measured stream height would now faithfully reproduce. The
          // mobile stylesheet therefore renders the CONDENSED content in all three
          // states: head row, the make chance, and the three score slots. The stat
          // rows and the rolling-ball scene are the two things a 44-px strip
          // cannot hold.
          <BirdieCard
            capsule={props.capsule}
            ring={props.ring}
            {...(props.rings ? { rings: props.rings } : {})}
            roundType={props.roundType}
            {...(props.thought === undefined ? {} : { thought: props.thought })}
            view={card}
            visible
          />
        ) : null}
      </div>

      {props.notice ? (
        <div
          aria-live="polite"
          className={`birdie-notice birdie-notice--${props.notice.tone} birdie-notice--persistent`}
          role="status"
        >
          {props.notice.message}
        </div>
      ) : null}

      {countRow.length > 0
        ? block(
            'count',
            <div
              className="birdie-stack__grid birdie-stack__grid--count"
              style={
                {
                  '--birdie-columns': round.length > 0 ? COUNT_COLUMNS_WITH_ROUND : COUNT_COLUMNS
                } as CSSProperties
              }
            >
              {countRow.map((tile, index) => renderTile(tile, index))}
            </div>
          )
        : null}

      {exact.length > 0
        ? block(
            'exact',
            <div
              className="birdie-stack__grid birdie-stack__grid--exact"
              style={{ '--birdie-columns': EXACT_COLUMNS } as CSSProperties}
            >
              {exact.map((tile, index) => renderTile(tile, countRow.length + index))}
            </div>
          )
        : null}
    </div>
  )
}
